import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { Messages } from "../../../../components";
import { COLORS } from "../../../../shared/theme/colorConfig";
import type { ApplicationData } from "../applicationState";
import type { HouseholdIncomeForm } from "./HouseholdIncome";
import type { AssetsForm } from "./Assets";
import type { RentalDetailsData } from "./RentalDetails";

export interface ReviewSignForm {
  certified: true;
  acceptedTerms: true;
  signatureName: string;
  signedAt: string;
}

interface ReviewSignProps {
  applicationData: ApplicationData;
  // Unit & lease context is NOT part of applicationData — direct-unit mode carries
  // it in the apply URL (unitID / moveInDate / leaseTerm) plus the unit query for
  // rent. Holding mode has no unit in context, so these arrive undefined and the
  // Unit & Lease block is omitted rather than showing a placeholder.
  unitNumber?: string;
  rent?: number | string;
  moveInDate?: string;
  leaseTerm?: string;
  onSubmit: (data: ReviewSignForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  // Backend submit error (e.g. "Unit is not available for lease at this time.")
  // surfaced by the parent so it renders above the submit button instead of the
  // submit silently failing.
  errorMessage?: string;
  onDismissError?: () => void;
  correctionSlot?: React.ReactNode;
}

const INCOME_KEYS: (keyof Omit<
  HouseholdIncomeForm,
  "otherIncomeDescription"
>)[] = [
  "employmentIncome",
  "selfEmployment",
  "cashWork",
  "unemployment",
  "vaGiBill",
  "militaryIncome",
  "publicAssistance",
  "ssRetirement",
  "ssiSsdi",
  "disabilityDeathBenefits",
  "workersComp",
  "cashAssistanceFromOthers",
  "billPaymentHelp",
  "trustsAnnuitiesPensions",
  "realEstatePropertyIncome",
  "onlineIncome",
  "educationAssistance",
  "unearnedIncomeMinors",
  "zeroIncomeMember",
  "otherIncomeNotListed",
];

const ASSET_KEYS: (keyof Omit<AssetsForm, "otherAssetDescription">)[] = [
  "checking",
  "savings",
  "internetBased",
  "debitCards",
  "moneyMarket",
  "cashOnHand",
  "brokerage",
  "capitalInvestments",
  "stocks",
  "bonds",
  "annuities",
  "cds",
  "cryptocurrency",
  "lifeInsurance",
  "trustFunds",
  "lumpSumAmounts",
  "safetyDepositBox",
  "realEstateProperty",
  "landContracts",
  "taxRefund",
  "crowdsourcing",
  "assetsGivenAway",
  "personalPropertyInvestment",
  "otherAssetsNotListed",
];

const maskSsn = (ssn?: string, noSsn?: boolean): string => {
  if (noSsn) return "—";
  if (!ssn) return "";
  const clean = ssn.replace(/\D/g, "");
  if (clean.length < 4) return "***-**-****";
  return `***-**-${clean.slice(-4)}`;
};

// A holding-mode application has no real unit in context: the API assigns the
// property's affordable holding unit (or the literal 'HOLD') with rent 0 and a
// synthesised move-in date. None of that is real lease economics, so it must never
// be quoted at the applicant — omit the block instead.
const HOLD_UNIT = "HOLD";
const isPlaceholderUnit = (unit?: string): boolean => {
  const normalized = (unit ?? "").trim();
  return normalized === "" || normalized.toUpperCase() === HOLD_UNIT;
};

// URL move-in params are ISO (YYYY-MM-DD); everything else on this screen renders
// MM/DD/YYYY. Left as-is when the value is not an ISO date.
const formatIsoDate = (value?: string): string => {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
};

const joinParts = (parts: (string | undefined)[]): string =>
  parts.filter((p) => p && String(p).trim()).join(" · ");

const incomeFrequencyLabel = (freq?: string): string => {
  switch (freq) {
    case "weekly":
      return "weekly";
    case "bi-weekly":
      return "every two weeks";
    case "semi-monthly":
      return "twice per month";
    case "monthly":
      return "monthly";
    case "annually":
      return "annually";
    case "week":
      return "per week";
    case "month":
      return "per month";
    default:
      return "";
  }
};

const formatAddress = (address?: {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string => {
  if (!address) return "";
  const cityStateZip = [
    address.city,
    [address.state, address.zip].filter(Boolean).join(" ").trim(),
  ]
    .filter((v) => v && String(v).trim())
    .join(", ");
  return [address.street, cityStateZip]
    .filter((v) => v && String(v).trim())
    .join(", ");
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right" }}>
      {value || "—"}
    </Typography>
  </Box>
);

// Group sub-header — same treatment the inline blocks in this file already use
// (COLORS.gray[600] / 700 / uppercase), extracted so the sections below don't
// repeat it ten more times.
const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      color: COLORS.gray[600],
      fontWeight: 700,
      letterSpacing: 0.8,
      mb: 1,
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

export const ReviewSign: React.FC<ReviewSignProps> = ({
  applicationData,
  unitNumber,
  rent,
  moveInDate,
  leaseTerm,
  onSubmit,
  onBack,
  isSaving = false,
  errorMessage = "",
  onDismissError,
  correctionSlot,
}) => {
  const { t } = useTranslation();

  const memberProfiles = applicationData.memberProfiles as
    | {
        firstName?: string;
        lastName?: string;
        phone?: string;
        dateOfBirth?: string;
        ssn?: string;
        noSsn?: boolean;
        willAnyoneElseLive?: "yes" | "no" | "";
        members?: Array<{
          firstName?: string;
          lastName?: string;
          relationship?: string;
          dateOfBirth?: string;
        }>;
      }
    | undefined;
  const studentDetails = applicationData.studentDetails as
    | { householdStatus?: string }
    | undefined;
  const specialStatus = applicationData.specialStatus as
    | { elderlyOrDisabled?: string }
    | undefined;
  const householdIncome = applicationData.householdIncome as
    | HouseholdIncomeForm
    | undefined;
  const assets = applicationData.assets as AssetsForm | undefined;
  // Rental history, emergency contact and vehicles all live under rentalDetails —
  // vehicles in particular are rentalDetails.vehicles, NOT top-level.
  const rentalDetails = applicationData.rentalDetails as
    | Partial<RentalDetailsData>
    | undefined;
  const uploadCenter = applicationData.uploadCenter as
    | {
        documents?: Record<
          string,
          Array<{ fileName: string; fileSize: number }>
        >;
        needsDocumentHelp?: boolean;
      }
    | undefined;
  const needsDocumentHelp = uploadCenter?.needsDocumentHelp === true;

  const primaryName = [memberProfiles?.firstName, memberProfiles?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const [certified, setCertified] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [signatureName, setSignatureName] = useState(primaryName);
  const [signatureTouched, setSignatureTouched] = useState(false);

  const signatureValid = signatureName.trim().length >= 3;
  const canSubmit = certified && acceptedTerms && signatureValid && !isSaving;

  const selectedIncome = INCOME_KEYS.filter((k) => householdIncome?.[k]);
  const selectedAssets = ASSET_KEYS.filter((k) => assets?.[k]);

  const currentAddress = rentalDetails?.currentAddress;
  const previousAddresses = rentalDetails?.previousAddresses ?? [];
  const emergencyContact = rentalDetails?.emergencyContact;
  const vehicles = rentalDetails?.vehicles ?? [];
  const hasRentalHistory = !!(
    currentAddress?.street ||
    currentAddress?.landlord ||
    previousAddresses.length > 0
  );
  const additionalJobs = householdIncome?.additionalJobs ?? [];
  const hasEmploymentDetails = !!(
    householdIncome?.employmentIncomeAmount ||
    householdIncome?.employmentIncomeFrequency ||
    householdIncome?.occupation ||
    householdIncome?.employerName ||
    householdIncome?.employerAddress ||
    householdIncome?.employerCityStateZip ||
    householdIncome?.employerWorkPhone ||
    householdIncome?.employmentIndustry ||
    householdIncome?.employmentStartDate ||
    householdIncome?.employmentEndDate ||
    householdIncome?.supervisorName ||
    householdIncome?.supervisorPhone ||
    additionalJobs.length > 0
  );
  const hasEmergencyContact = !!(
    emergencyContact?.declined ||
    emergencyContact?.name ||
    emergencyContact?.phone
  );

  // Unit & lease economics — only shown for a real unit. See isPlaceholderUnit.
  const showUnitAndLease = !isPlaceholderUnit(unitNumber);
  const rentValue = Number(rent);
  const showRent = Number.isFinite(rentValue) && rentValue > 0;
  const documentCount = uploadCenter?.documents
    ? Object.values(uploadCenter.documents).reduce(
        (sum, arr) => sum + arr.length,
        0
      )
    : 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      certified: true,
      acceptedTerms: true,
      signatureName: signatureName.trim(),
      signedAt: new Date().toISOString(),
    });
  };

  const householdStatusLabel = (() => {
    const s = studentDetails?.householdStatus;
    if (!s) return "—";
    return t(`affordable.application.reviewSign.studentStatus.${s}`, {
      defaultValue: s,
    });
  })();

  return (
    <Box>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.reviewSign.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("affordable.application.reviewSign.subtitle")}
        </Typography>

        {/* Unit & Lease — omitted entirely in holding mode (no real unit) */}
        {showUnitAndLease && (
          <>
            <Box sx={{ mb: 2 }}>
              <GroupLabel>
                {t("affordable.application.reviewSign.unitLease.title")}
              </GroupLabel>
              <Row
                label={t("affordable.application.reviewSign.unitLease.unit")}
                value={unitNumber}
              />
              {showRent && (
                <Row
                  label={t("affordable.application.reviewSign.unitLease.rent")}
                  value={`$${rentValue.toFixed(2)}`}
                />
              )}
              {moveInDate && (
                <Row
                  label={t(
                    "affordable.application.reviewSign.unitLease.moveInDate"
                  )}
                  value={formatIsoDate(moveInDate)}
                />
              )}
              {Number(leaseTerm) > 0 && (
                <Row
                  label={t("affordable.application.reviewSign.unitLease.term")}
                  value={t(
                    "affordable.application.reviewSign.unitLease.termMonths",
                    { count: Number(leaseTerm) }
                  )}
                />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Applicant Info */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            {t("account.leaseApplication.applicantInfo")}
          </Typography>
          <Row
            label={t("formInput.firstName")}
            value={memberProfiles?.firstName}
          />
          <Row
            label={t("formInput.lastName")}
            value={memberProfiles?.lastName}
          />
          <Row
            label={t("account.leaseApplication.phone")}
            value={memberProfiles?.phone}
          />
          <Row
            label={t("account.leaseApplication.birthdate")}
            value={memberProfiles?.dateOfBirth}
          />
          <Row
            label={t("account.leaseApplication.ssn")}
            value={maskSsn(memberProfiles?.ssn, memberProfiles?.noSsn)}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Household Members */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            {t("affordable.application.reviewSign.householdMembers")}
          </Typography>
          <Row
            label={t("affordable.application.memberProfiles.willAnyoneLive")}
            value={
              memberProfiles?.willAnyoneElseLive === "yes" ||
              (memberProfiles?.willAnyoneElseLive !== "no" &&
                (memberProfiles?.members?.length ?? 0) > 0)
                ? t("affordable.application.reviewSign.yesNo.yes")
                : memberProfiles?.willAnyoneElseLive === "no"
                ? t("affordable.application.reviewSign.yesNo.no")
                : t("affordable.application.reviewSign.notAnswered")
            }
          />
          {memberProfiles?.willAnyoneElseLive === "yes" ||
          (memberProfiles?.willAnyoneElseLive !== "no" &&
            memberProfiles?.members &&
            memberProfiles.members.length > 0) ? (
            memberProfiles.members?.map((m, idx) => (
              <Row
                key={idx}
                label={
                  `${m.firstName || ""} ${m.lastName || ""}`.trim() ||
                  `#${idx + 1}`
                }
                value={`${m.relationship || "—"} · ${m.dateOfBirth || "—"}`}
              />
            ))
          ) : memberProfiles?.willAnyoneElseLive === "no" ? null : (
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.reviewSign.noHouseholdMembers")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Rental History — current address + landlord contact, then priors */}
        {hasRentalHistory && (
          <>
            <Box sx={{ mb: 2 }}>
              <GroupLabel>
                {t("affordable.application.rentalDetails.rentalHistoryTitle")}
              </GroupLabel>
              <Row
                label={t("affordable.application.reviewSign.address")}
                value={formatAddress(currentAddress)}
              />
              <Row
                label={t("affordable.application.rentalDetails.moveInDate")}
                value={currentAddress?.moveInDate}
              />
              <Row
                label={t("affordable.application.rentalDetails.landlord")}
                value={currentAddress?.landlord}
              />
              <Row
                label={t("affordable.application.rentalDetails.landlordPhone")}
                value={currentAddress?.landlordPhone}
              />
              <Row
                label={t("affordable.application.rentalDetails.landlordEmail")}
                value={currentAddress?.landlordEmail}
              />
              <Row
                label={t(
                  "affordable.application.rentalDetails.reasonForLeaving"
                )}
                value={currentAddress?.reasonForLeaving}
              />
              {previousAddresses.map((a, idx) => (
                <Row
                  key={idx}
                  label={t(
                    "affordable.application.rentalDetails.previousAddressHeading",
                    { number: idx + 1 }
                  )}
                  value={joinParts([
                    formatAddress(a),
                    a?.moveInDate && a?.moveOutDate
                      ? `${a.moveInDate} – ${a.moveOutDate}`
                      : a?.moveInDate,
                    a?.landlord,
                    a?.landlordPhone,
                    a?.landlordEmail,
                  ])}
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Vehicles — the Yes/No is stored even when the answer is No */}
        <Box sx={{ mb: 2 }}>
          <GroupLabel>
            {t("affordable.application.rentalDetails.vehiclesTitle")}
          </GroupLabel>
          <Row
            label={t("affordable.application.rentalDetails.hasVehicles")}
            value={
              rentalDetails?.hasVehicles === true ||
              (rentalDetails?.hasVehicles == null && vehicles.length > 0)
                ? t("affordable.application.reviewSign.yesNo.yes")
                : rentalDetails?.hasVehicles === false
                ? t("affordable.application.reviewSign.yesNo.no")
                : t("affordable.application.reviewSign.notAnswered")
            }
          />
          {(rentalDetails?.hasVehicles === true ||
            (rentalDetails?.hasVehicles == null && vehicles.length > 0)) &&
            (vehicles.length > 0 ? (
              vehicles.map((v, idx) => (
                <Row
                  key={idx}
                  label={
                    [v?.year, v?.make, v?.model]
                      .filter(Boolean)
                      .join(" ")
                      .trim() ||
                    `${t(
                      "affordable.application.rentalDetails.vehicleLabel"
                    )} ${idx + 1}`
                  }
                  value={joinParts([
                    v?.color,
                    v?.licensePlate,
                    v?.licenseState,
                  ])}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("affordable.application.reviewSign.noVehicles")}
              </Typography>
            ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Emergency Contact */}
        {hasEmergencyContact && (
          <>
            <Box sx={{ mb: 2 }}>
              <GroupLabel>
                {t(
                  "affordable.application.rentalDetails.emergencyContactTitle"
                )}
              </GroupLabel>
              {emergencyContact?.declined ? (
                <Typography variant="body2" color="text.secondary">
                  {t(
                    "affordable.application.reviewSign.emergencyContactDeclined"
                  )}
                </Typography>
              ) : (
                <>
                  <Row
                    label={t(
                      "affordable.application.rentalDetails.emergencyName"
                    )}
                    value={emergencyContact?.name}
                  />
                  <Row
                    label={t(
                      "affordable.application.rentalDetails.emergencyRelationship"
                    )}
                    value={emergencyContact?.relationship}
                  />
                  <Row
                    label={t(
                      "affordable.application.rentalDetails.emergencyPhone"
                    )}
                    value={emergencyContact?.phone}
                  />
                  <Row
                    label={t("affordable.application.reviewSign.address")}
                    value={formatAddress(emergencyContact)}
                  />
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Student Status */}
        <Box sx={{ mb: 2 }}>
          <Row
            label={t("affordable.application.steps.studentStatus")}
            value={householdStatusLabel}
          />
          <Row
            label={t("affordable.application.steps.specialStatus")}
            value={
              specialStatus?.elderlyOrDisabled
                ? t(
                    `affordable.application.reviewSign.yesNo.${specialStatus.elderlyOrDisabled}`,
                    { defaultValue: specialStatus.elderlyOrDisabled }
                  )
                : "—"
            }
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Marital & Family — captured in HouseholdIncome step */}
        {(householdIncome?.maritalStatus ||
          householdIncome?.childSupportStatus ||
          ((householdIncome?.cashAssistanceFromOthers === true ||
            householdIncome?.billPaymentHelp === true) &&
            householdIncome?.recurringGiftSourceName)) && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.gray[600],
                fontWeight: 700,
                letterSpacing: 0.8,
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              Marital & Family
            </Typography>
            {householdIncome?.maritalStatus && (
              <Row
                label="Marital status"
                value={householdIncome.maritalStatus}
              />
            )}
            {(householdIncome?.maritalStatus === "Separated" ||
              householdIncome?.maritalStatus === "Estranged") && (
              <>
                {householdIncome.spouseName && (
                  <Row label="Spouse" value={householdIncome.spouseName} />
                )}
                <Row
                  label="Receiving spousal contribution"
                  value={
                    householdIncome.receivingSpousalContribution
                      ? `Yes${
                          householdIncome.spousalContributionAmount
                            ? ` ($${householdIncome.spousalContributionAmount})`
                            : ""
                        }`
                      : "No"
                  }
                />
                <Row
                  label="Awarded income (alimony / child support / survivor benefits)"
                  value={householdIncome.awardedIncome ? "Yes" : "No"}
                />
                <Row
                  label="Legal/court docs on file"
                  value={householdIncome.possessLegalDocs ? "Yes" : "No"}
                />
              </>
            )}
            {householdIncome?.childSupportStatus && (
              <>
                <Row
                  label="Child support — custody of"
                  value={
                    (householdIncome.childSupportChildIds ?? []).join(", ") ||
                    "—"
                  }
                />
                <Row
                  label="Child support — status"
                  value={`(${householdIncome.childSupportStatus}) ${
                    (
                      {
                        a: "Court-ordered, currently receiving",
                        b: "Court-ordered, not receiving and not anticipating",
                        c: "Not court-ordered, no docs; receives cash",
                        d: "Never received; anticipating in next 12 months",
                        e: "Never received, not anticipating",
                      } as Record<string, string>
                    )[householdIncome.childSupportStatus] ??
                    householdIncome.childSupportStatus
                  }`}
                />
                {householdIncome.childSupportAmount && (
                  <Row
                    label="Child support — amount"
                    value={`$${householdIncome.childSupportAmount}${
                      householdIncome.childSupportStatus === "a" ||
                      householdIncome.childSupportStatus === "b"
                        ? householdIncome.childSupportFrequency
                          ? ` per ${householdIncome.childSupportFrequency}`
                          : ""
                        : householdIncome.childSupportStatus === "c" ||
                          householdIncome.childSupportStatus === "d"
                        ? " per month"
                        : ""
                    }`}
                  />
                )}
              </>
            )}
            {(householdIncome?.cashAssistanceFromOthers === true ||
              householdIncome?.billPaymentHelp === true) &&
              householdIncome?.recurringGiftSourceName && (
                <>
                  <Row
                    label="Recurring gift source"
                    value={[
                      householdIncome.recurringGiftSourceName,
                      householdIncome.recurringGiftSourceEmail,
                      householdIncome.recurringGiftSourcePhone,
                    ]
                      .filter((v) => v && String(v).trim())
                      .join(" · ")}
                  />
                </>
              )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Income */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            {t("affordable.application.steps.householdIncome")}
          </Typography>
          {selectedIncome.length > 0 ? (
            selectedIncome.map((k) => (
              <Typography key={k} variant="body2" sx={{ py: 0.25 }}>
                • {t(`affordable.application.householdIncome.items.${k}.title`)}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.reviewSign.noneSelected")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Employment — employer details are captured on the income step */}
        {hasEmploymentDetails && (
          <>
            <Box sx={{ mb: 2 }}>
              <GroupLabel>
                {t("affordable.application.reviewSign.employment")}
              </GroupLabel>
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.occupationLabel"
                )}
                value={householdIncome?.occupation}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.industryLabel"
                )}
                value={householdIncome?.employmentIndustry}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.employmentIncomeAmountLabel"
                )}
                value={
                  householdIncome?.employmentIncomeAmount
                    ? `$${householdIncome.employmentIncomeAmount}${
                        incomeFrequencyLabel(
                          householdIncome.employmentIncomeFrequency
                        )
                          ? ` ${incomeFrequencyLabel(
                              householdIncome.employmentIncomeFrequency
                            )}`
                          : ""
                      }`
                    : ""
                }
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.employerNameLabel"
                )}
                value={householdIncome?.employerName}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.employerAddressLabel"
                )}
                value={householdIncome?.employerAddress}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.employerCityStateZipLabel"
                )}
                value={householdIncome?.employerCityStateZip}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.employerWorkPhoneLabel"
                )}
                value={householdIncome?.employerWorkPhone}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.startDateLabel"
                )}
                value={householdIncome?.employmentStartDate}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.endDateLabel"
                )}
                value={householdIncome?.employmentEndDate}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.supervisorNameLabel"
                )}
                value={householdIncome?.supervisorName}
              />
              <Row
                label={t(
                  "affordable.application.householdIncome.cashTip.supervisorPhoneLabel"
                )}
                value={householdIncome?.supervisorPhone}
              />
              {additionalJobs.map((job, index) => (
                <Box key={job.id || index} sx={{ mt: 2 }}>
                  <GroupLabel>
                    {t(
                      "affordable.application.householdIncome.additionalJobs.jobLabel",
                      { n: index + 2 }
                    )}
                  </GroupLabel>
                  <Row
                    label={t(
                      "affordable.application.householdIncome.additionalJobs.kindLabel"
                    )}
                    value={t(
                      `affordable.application.householdIncome.additionalJobs.kindOptions.${job.kind}`
                    )}
                  />
                  <Row
                    label={t(
                      "affordable.application.householdIncome.cashTip.occupationLabel"
                    )}
                    value={job.occupation}
                  />
                  <Row
                    label={t(
                      "affordable.application.householdIncome.additionalJobs.amountLabel"
                    )}
                    value={
                      job.amount
                        ? `$${job.amount}${
                            incomeFrequencyLabel(job.frequency)
                              ? ` ${incomeFrequencyLabel(job.frequency)}`
                              : ""
                          }`
                        : ""
                    }
                  />
                  <Row
                    label={t(
                      "affordable.application.householdIncome.cashTip.employerNameLabel"
                    )}
                    value={job.employerName}
                  />
                  <Row
                    label={t(
                      "affordable.application.householdIncome.cashTip.employerAddressLabel"
                    )}
                    value={job.employerAddress}
                  />
                  <Row
                    label={t(
                      "affordable.application.householdIncome.cashTip.startDateLabel"
                    )}
                    value={job.employmentStartDate}
                  />
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Assets */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: COLORS.gray[600],
              fontWeight: 700,
              letterSpacing: 0.8,
              mb: 1,
              textTransform: "uppercase",
            }}
          >
            {t("affordable.application.steps.assets")}
          </Typography>
          {selectedAssets.length > 0 ? (
            selectedAssets.map((k) => (
              <Typography key={k} variant="body2" sx={{ py: 0.25 }}>
                • {t(`affordable.application.assets.items.${k}.title`)}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("affordable.application.reviewSign.noneSelected")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Documents */}
        <Row
          label={t("affordable.application.steps.uploadCenter")}
          value={t("affordable.application.reviewSign.documentsCount", {
            count: documentCount,
          })}
        />
        {needsDocumentHelp && (
          <Row
            label={t(
              "affordable.application.reviewSign.needsDocumentHelpLabel"
            )}
            value={
              <Box component="span" sx={{ color: "warning.main" }}>
                {t("affordable.application.reviewSign.needsDocumentHelpValue")}
              </Box>
            }
          />
        )}
      </RegisterReviewSection>

      {/* Certification */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.reviewSign.certification.title")}
        </RegisterReviewSectionTitle>
        <FormControlLabel
          control={
            <Checkbox
              checked={certified}
              onChange={(e) => setCertified(e.target.checked)}
            />
          }
          label={t("affordable.application.reviewSign.certification.label")}
        />
      </RegisterReviewSection>

      {/* Terms & Conditions */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.reviewSign.terms.title")}
        </RegisterReviewSectionTitle>
        <Box
          sx={{
            maxHeight: 200,
            overflowY: "auto",
            p: 2,
            border: `1px solid ${COLORS.gray[200]}`,
            borderRadius: 1,
            backgroundColor: COLORS.gray[50],
            mb: 1.5,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "pre-line", lineHeight: 1.6 }}
          >
            {t("affordable.application.reviewSign.terms.body")}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
          }
          label={t("affordable.application.reviewSign.terms.accept")}
        />
      </RegisterReviewSection>

      {/* Signature */}
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.reviewSign.signature.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("affordable.application.reviewSign.signature.instructions")}
        </Typography>
        <TextField
          fullWidth
          value={signatureName}
          onChange={(e) => {
            setSignatureName(e.target.value);
            setSignatureTouched(true);
          }}
          onBlur={() => setSignatureTouched(true)}
          error={signatureTouched && !signatureValid}
          helperText={
            signatureTouched && !signatureValid
              ? t("affordable.application.reviewSign.signature.required")
              : undefined
          }
          label={t("affordable.application.reviewSign.signature.label")}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {t("affordable.application.reviewSign.signature.date")}:{" "}
          {new Date().toLocaleDateString("en-US")}
        </Typography>
      </RegisterReviewSection>

      {correctionSlot}
      {errorMessage !== "" && !correctionSlot && (
        <Messages
          messageHeading={t("affordable.application.reviewSign.title")}
          messageContent={errorMessage}
          closeEvent={onDismissError}
          type="error"
          topMargin={3}
          bottomMargin={0}
        />
      )}

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isSaving}
          startIcon={<ArrowBackIcon />}
        >
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || !!correctionSlot}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckCircleOutlineIcon />
            )
          }
        >
          {t("affordable.application.reviewSign.submitButton")}
        </Button>
      </Box>
    </Box>
  );
};
